import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, PlayIcon } from 'lucide-react';
import { Reveal } from './Reveal';
import { testimonials, videoStories } from '../data/homepage';
const MAX_SLIDES = 3;
export function Testimonials() {
  const slides = testimonials.slice(0, MAX_SLIDES);
  const [index, setIndex] = useState(0);
  const reduce = useReducedMotion();
  const multi = slides.length > 1;
  const current = slides[index];
  const go = (dir: number) => setIndex(i => (i + dir + slides.length) % slides.length);
  return <section aria-labelledby="testimonial-heading" className="relative isolate overflow-hidden bg-navy pb-24 pt-12 text-paper md:pb-32" data-id="element-200" data-mp="2x1uquj1b313z">
      <img src="https://cdn.magicpatterns.com/patterns/generated-images/4bffce18-904b-4be4-82ef-5657cb6e1f5f.jpg" alt="A pair of worn combat boots beside a child's small sneakers on a front-door mat." width={2880} height={1620} loading="lazy" className="absolute inset-0 -z-20 h-full w-full object-cover object-bottom" data-id="element-201" data-mp="2s3jjqg2eb4pg" />
      <div aria-hidden="true" className="absolute inset-0 -z-10 bg-navy/85" data-id="element-202" data-mp="m5swuah3fokz" />
      <h2 id="testimonial-heading" className="sr-only" data-id="element-203" data-mp="78k4pejyugs7">
        What veterans say
      </h2>
      <Reveal className="mx-auto max-w-4xl px-6 text-center md:px-10" data-id="element-204" data-mp="c6bx09oiw4wx">
        <div role="region" aria-roledescription="carousel" aria-label="Testimonials" data-id="element-205" data-mp="zwfxi86ezojp">
          <span aria-hidden="true" className="block font-serif text-8xl leading-none text-gold md:text-9xl" data-id="element-206" data-mp="zyiujc5zihxx">
            “
          </span>
          <div className="relative min-h-[14rem] md:min-h-[12rem]" aria-live="polite" data-id="element-207" data-mp="q7eiolblvd7b">
            <AnimatePresence mode="wait" initial={false} data-id="element-208" data-mp="v5fy8w9toue1">
              <motion.figure key={index} role="group" aria-roledescription="slide" aria-label={`${index + 1} of ${slides.length}`} initial={reduce ? false : {
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={reduce ? {
              opacity: 0
            } : {
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.25,
              ease: [0.23, 1, 0.32, 1]
            }} data-id="element-209" data-mp="v3wh73gcefb7">
                <blockquote className="font-serif text-2xl font-medium leading-snug md:text-4xl md:leading-snug" data-id="element-210" data-mp="1og9gg234nj37">
                  {current.quote}
                </blockquote>
                <figcaption className="mt-10 flex items-center justify-center gap-4 text-base font-semibold tracking-[0.15em] text-gold" data-id="element-211" data-mp="1ejog3u21yw5p">
                  <span aria-hidden="true" className="h-px w-8 bg-gold" data-id="element-212" data-mp="19qvbd66l3ntm" />
                  {current.attribution.toUpperCase()}
                  <span aria-hidden="true" className="h-px w-8 bg-gold" data-id="element-213" data-mp="14qz32uquta89" />
                </figcaption>
              </motion.figure>
            </AnimatePresence>
          </div>

          {multi && <div className="mt-12 flex items-center justify-center gap-6" data-id="element-214" data-mp="vuhqdn2ud4ae">
              <button type="button" onClick={() => go(-1)} aria-label="Previous testimonial" className="flex h-12 w-12 items-center justify-center rounded-full border border-paper/30 transition-colors duration-150 ease-out hover:border-gold hover:text-gold" data-id="element-215" data-mp="quc64rywik2c">
                <ChevronLeftIcon className="h-6 w-6" data-id="element-216" data-mp="3ggf3ail5wdcl" />
              </button>
              <div className="flex gap-3" data-id="element-217" data-mp="3geceu2znqs9z">
                {slides.map((_, i) => <button key={i} type="button" onClick={() => setIndex(i)} aria-label={`Show testimonial ${i + 1}`} aria-current={i === index} className="flex h-12 w-12 items-center justify-center rounded-full" data-id="element-218" data-mp="2js90803w1t0f" data-mpi={i}>
                    <span aria-hidden="true" className={`h-3 w-3 rounded-full transition-colors duration-150 ease-out ${i === index ? 'bg-gold' : 'bg-paper/40'}`} data-id="element-219" data-mp="210q2a7oq1559" />
                  </button>)}
              </div>
              <button type="button" onClick={() => go(1)} aria-label="Next testimonial" className="flex h-12 w-12 items-center justify-center rounded-full border border-paper/30 transition-colors duration-150 ease-out hover:border-gold hover:text-gold" data-id="element-220" data-mp="1gzn3iv2h7722">
                <ChevronRightIcon className="h-6 w-6" data-id="element-221" data-mp="1zobhex2jkc4c" />
              </button>
            </div>}
        </div>
      </Reveal>

      <div className="mx-auto mt-20 max-w-6xl px-6 md:px-10" data-id="element-222" data-mp="2ekj2dzlrxhko">
        <h3 className="sr-only" data-id="element-223" data-mp="29mhjbeocli97">Veteran stories on video</h3>
        <ul className="grid gap-6 md:grid-cols-3" data-id="element-224" data-mp="29n0veerbhcfb">
          {videoStories.map((story, i) => <li key={story.id} data-id="element-225" data-mp="k82xp38mmbhx" data-mpi={i}>
              <Reveal delay={i * 0.05} data-id="element-226" data-mp="f8gbw5eqfmxi">
                <div role="img" aria-label={`Video placeholder: ${story.label}`} className="flex aspect-video flex-col items-center justify-center gap-4 rounded-sm border border-dashed border-paper/35 bg-navy-deep/80 p-6 text-center" data-id="element-227" data-mp="f9hxv4xadtu0">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full border border-gold" data-id="element-228" data-mp="2h57b5sb48ukr">
                    <PlayIcon aria-hidden="true" className="ml-1 h-6 w-6 fill-gold text-gold" data-id="element-229" data-mp="2m3imk7h27d53" />
                  </span>
                  <p className="text-lg text-paper/90" data-id="element-230" data-mp="287ewmqd7xqyf">{story.label}</p>
                </div>
              </Reveal>
            </li>)}
        </ul>
      </div>
    </section>;
}